import AsyncStorage from '@react-native-async-storage/async-storage';
import { ZCLUB_API_URL } from '../config/api';

// Base URL for e-stamps API
const ESTAMPS_BASE_URL = `${ZCLUB_API_URL}/zclubestamps`;

/**
 * Test e-stamps API connectivity
 */
export const testEstampsApi = async () => {
  try {
    const response = await fetch(`${ESTAMPS_BASE_URL}/test`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const data = await response.json();
    
    return {
      success: data.success || false,
      message: data.message || 'Test failed',
      data: data
    };
  } catch (error) {
    console.error('Error testing e-stamps API:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Get active e-stamp events (no authentication required)
 */
export const getEstampEvents = async (brandId = null) => {
  try {
    // Build URL with brand_id parameter if provided and valid
    let url = `${ESTAMPS_BASE_URL}/events`;
    if (brandId && brandId !== '0' && brandId !== 0) {
      url += `?brand_id=${encodeURIComponent(brandId)}`;
    }
    
    // console.log('Fetching e-stamp events from:', url);
    console.log('Fetching e-stamp events');
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const data = await response.json();
    
    // console.log('', data);
    
    if (data.success) {
      return {
        success: true,
        data: data.data || []
      };
    } else {
      // Handle Brand not found error specifically
      if (response.status === 404 && data.messages && data.messages.error === 'Brand not found') {
        console.warn('Brand not found, falling back to all events');
        // Retry without brand_id parameter
        return await getEstampEvents(null);
      }
      
      return {
        success: false,
        error: data.message || 'Failed to fetch e-stamp events',
        data: []
      };
    }
  } catch (error) {
    console.error('Error fetching e-stamp events:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

/**
 * Get user's e-stamp dashboard data (authentication required)
 */
export const getEstampDashboard = async (brandId = null) => {
  try {
    const sessionId = await AsyncStorage.getItem('session_id');
    const rememberToken = await AsyncStorage.getItem('remember_token');
    
    if (!sessionId) {
      return {
        success: false,
        error: 'No session ID found',
        data: null
      };
    }
    
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-ID': sessionId,
    };
    
    if (rememberToken) {
      headers['X-Remember-Token'] = rememberToken;
    }
    
    // Build URL with brand_id parameter if provided and valid
    let url = `${ESTAMPS_BASE_URL}/dashboard`;
    if (brandId && brandId !== '0' && brandId !== 0) {
      url += `?brand_id=${encodeURIComponent(brandId)}`;
    }
    
    // console.log('Fetching e-stamp dashboard from:', url);
    console.log('Fetching e-stamp dashboard');
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    const data = await response.json();
    
    // console.log('E-stamp dashboard response:', data);
    
    if (data.success) {
      return {
        success: true,
        data: data.data
      };
    } else {
      // Handle Brand not found error specifically
      if (response.status === 404 && data.messages && data.messages.error === 'Brand not found') {
        console.warn('Brand not found, falling back to general dashboard');
        // Retry without brand_id parameter
        return await getEstampDashboard(null);
      }
      
      return {
        success: false,
        error: data.message || 'Failed to fetch dashboard data',
        data: null
      };
    }
  } catch (error) {
    console.error('Error fetching e-stamp dashboard:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Get collection details for a specific event (authentication required)
 * @param {number} eventId - The ID of the e-stamp event
 */
export const getEstampCollection = async (eventId) => {
  try {
    const sessionId = await AsyncStorage.getItem('session_id');
    const rememberToken = await AsyncStorage.getItem('remember_token');
    
    if (!sessionId) {
      return {
        success: false,
        error: 'No session ID found',
        data: null
      };
    }
    
    if (!eventId) {
      return {
        success: false,
        error: 'Event ID is required',
        data: null
      };
    }
    
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-ID': sessionId,
    };
    
    if (rememberToken) {
      headers['X-Remember-Token'] = rememberToken;
    }
    
    console.log('Fetching e-stamp collection from:', `${ESTAMPS_BASE_URL}/collection/${eventId}`);
    
    const response = await fetch(`${ESTAMPS_BASE_URL}/collection/${eventId}`, {
      method: 'GET',
      headers,
    });
    const data = await response.json();
    
    console.log('E-stamp collection response:', data);
    
    if (data.success) {
      return {
        success: true,
        data: data.data
      };
    } else {
      return {
        success: false,
        error: data.message || 'Failed to fetch collection data',
        data: null
      };
    }
  } catch (error) {
    console.error('Error fetching e-stamp collection:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Redeem reward for a completed e-stamp collection (authentication required)
 * @param {number} eventId - The ID of the event for which to redeem e-stamps
 */
export const redeemEstampReward = async (eventId) => {
  try {
    const sessionId = await AsyncStorage.getItem('session_id');
    const rememberToken = await AsyncStorage.getItem('remember_token');
    
    if (!sessionId) {
      return {
        success: false,
        error: 'No session ID found',
        data: null
      };
    }
    
    if (!eventId) {
      return {
        success: false,
        error: 'Event ID is required',
        data: null
      };
    }
    
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-ID': sessionId,
    };
    
    if (rememberToken) {
      headers['X-Remember-Token'] = rememberToken;
    }
    
    console.log('Redeeming e-stamp reward from:', `${ESTAMPS_BASE_URL}/redeem/${eventId}`);
    
    const response = await fetch(`${ESTAMPS_BASE_URL}/redeem/${eventId}`, {
      method: 'POST',
      headers,
    });
    const data = await response.json();
    
    console.log('E-stamp redeem response:', data);
    
    // Handle success response (can have either success: true OR status: "success")
    if (data.success === true || data.status === 'success') {
      return {
        success: true,
        message: data.message || 'Reward successfully redeemed',
        data: data.data
      };
    }
    
    // Handle error response (has status: "error")
    if (data.status === 'error') {
      return {
        success: false,
        error: data.message || 'Failed to redeem reward',
        data: data.data || null
      };
    }
    
    // Handle legacy error response format (has success: false)
    if (data.success === false) {
      return {
        success: false,
        error: data.message || data.error || 'Failed to redeem reward',
        data: data.data || null
      };
    }
    
    // Fallback for unexpected response format
    return {
      success: false,
      error: data.message || data.error || 'Failed to redeem reward',
      data: null
    };
  } catch (error) {
    console.error('Error redeeming e-stamp reward:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Get user's e-stamp transaction history (authentication required)
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Items per page (default: 20)
 * @param {string} type - Transaction type filter ('masuk', 'keluar', or null for all)
 */
export const getEstampHistory = async (page = 1, limit = 20, type = null) => {
  try {
    const sessionId = await AsyncStorage.getItem('session_id');
    const rememberToken = await AsyncStorage.getItem('remember_token');
    
    if (!sessionId) {
      return {
        success: false,
        error: 'No session ID found',
        data: null
      };
    }
    
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-ID': sessionId,
    };
    
    if (rememberToken) {
      headers['X-Remember-Token'] = rememberToken;
    }
    
    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());
    if (type) {
      queryParams.append('type', type);
    }
    
    const url = `${ESTAMPS_BASE_URL}/history?${queryParams.toString()}`;
    console.log('Fetching e-stamp history');
    // console.log('Fetching e-stamp history from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    const data = await response.json();
    
    // console.log('E-stamp history response:', data);
    
    if (data.success) {
      // Process the response data to handle the new consistent structure
      const processedData = (data.data || []).map(transaction => {
        // Base fields that are always present
        const processedTransaction = {
          id: transaction.id || null,
          amount: transaction.amount || 0,
          status: transaction.status || 'unknown',
          created_date: transaction.created_date || null,
          updated_date: transaction.updated_date || null,
          timestamp: transaction.timestamp || null
        };
        
        // Brand fields - only present when brand_id exists and is not '0'
        if (transaction.brand_id && transaction.brand_id !== '0') {
          processedTransaction.brand_id = transaction.brand_id;
          processedTransaction.brand_name = transaction.brand_name || null;
          processedTransaction.brand_logo = transaction.brand_logo || null;
          processedTransaction.description = transaction.description || null;
        }
        
        // Add legacy fields for backward compatibility if needed
        processedTransaction.jumlah_estamp = processedTransaction.amount;
        
        return processedTransaction;
      });
      
      return {
        success: true,
        data: processedData,
        pagination: data.pagination || null
      };
    } else {
      return {
        success: false,
        error: data.message || 'Failed to fetch transaction history',
        data: [],
        pagination: null
      };
    }
  } catch (error) {
    console.error('Error fetching e-stamp history:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      pagination: null
    };
  }
};

// Legacy function for backward compatibility
export const getAvailableEstamps = getEstampEvents;